import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { openInAppBrowser, isMobile } from "@/lib/mobile";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NearestProsMap from "@/components/NearestProsMap";
import RoyalProClassBadge from "@/components/RoyalProClassBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ArrowRight, CheckCircle2, Heart, Coins } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { format } from "date-fns";

const CITIES = ["Kingston", "Portmore", "Spanish Town", "Montego Bay", "Ocho Rios", "Mandeville"];
const TIME_SLOTS = ["08:00", "10:00", "12:00", "14:00", "16:00", "18:00", "20:00"];
const HOURS_OPTIONS = [1, 2, 3, 4, 5, 6, 8, 10, 12];

export default function BookingPage() {
    const { serviceId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [date, setDate] = useState(new Date(Date.now() + 24 * 60 * 60 * 1000));
    const [time, setTime] = useState("10:00");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("Kingston");
    const [notes, setNotes] = useState("");
    const [express, setExpress] = useState(false);
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [hours, setHours] = useState(4);
    const [childAge, setChildAge] = useState("");
    const [overnight, setOvernight] = useState(false);
    const [groceryBudget, setGroceryBudget] = useState("");
    const [groceryItems, setGroceryItems] = useState("");
    const [transportQuote, setTransportQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [nearestPros, setNearestPros] = useState(null);
    const [selectedProviderId, setSelectedProviderId] = useState(null);
    const [providers, setProviders] = useState([]);
    const [trustedIds, setTrustedIds] = useState([]);
    const [promoCode, setPromoCode] = useState("");
    const [promoApplied, setPromoApplied] = useState(null); // {code, override_price, title}
    const [promoChecking, setPromoChecking] = useState(false);
    // Cleaning property-size selector (CEO directive Feb 2026)
    const [bedrooms, setBedrooms] = useState(1);
    const [deepClean, setDeepClean] = useState(false);
    // SMART Coin redemption opt-in
    const [redeemCoins, setRedeemCoins] = useState(false);

    useEffect(() => {
        api.get(`/services/${serviceId}`)
            .then(({ data }) => setService(data))
            .catch(() => toast.error("Service not found"))
            .finally(() => setLoading(false));
    }, [serviceId]);

    // Load childcare providers + trusted-sitter list once we know the category
    useEffect(() => {
        if (!service?.supports_childcare_options) return;
        api.get("/providers", { params: { category: service.category } })
            .then(({ data }) => setProviders(Array.isArray(data) ? data : []))
            .catch(() => {});
        api.get("/users/me/trusted")
            .then(({ data }) => setTrustedIds((data || []).map((p) => p.id)))
            .catch(() => {});
    }, [service]);

    // Debounced live transport quote (distance + ETA + fee) when address/city changes
    useEffect(() => {
        if (!address || address.trim().length < 4) {
            setTransportQuote(null);
            setNearestPros(null);
            return;
        }
        const handle = setTimeout(async () => {
            try {
                setQuoteLoading(true);
                const { data } = await api.post("/transport/quote", { address, city });
                setTransportQuote(data);
            } catch {
                setTransportQuote(null);
            } finally {
                setQuoteLoading(false);
            }
            // Nearest pros (best-effort, runs in parallel — silent on failure)
            if (service?.category) {
                api.get("/providers/nearest", {
                    params: { address, city, category: service.category, limit: 3 },
                })
                    .then(({ data }) => setNearestPros(data))
                    .catch(() => setNearestPros(null));
            }
        }, 700);
        return () => clearTimeout(handle);
    }, [address, city, service?.category]);

    const isHourly = service?.pricing_model === "hourly";
    const isChildcare = service?.supports_childcare_options;
    const isGrocery = service?.supports_grocery_options;
    const isNegotiable = service?.is_negotiable === true;
    const hasAddons = (service?.add_ons || []).length > 0;

    const transportFee = transportQuote?.transport_fee ?? 1000; // default until quote arrives

    // Royal Protection & Insurance — mirrors backend pricing.py rules.
    const royalProtectionRule = (category) => {
        const flat = ["Cleaning", "Handyman", "Beauty", "Laundry"];
        const pct = {
            Childcare: 0.125,
            Chef: 0.20,
            Moving: 0.10,
            Auto: 0.10,
            Transport: 0.10,
            Decor: 0.15,
            Grocery: 0.15,
        };
        if (flat.includes(category)) return { kind: "flat", value: 1000 };
        return { kind: "percent", value: pct[category] ?? 0.15 };
    };

    const isCleaning = service?.category === "Cleaning";
    const isDeepCleanService = (service?.name || "").toLowerCase().includes("deep");

    const { subtotal, royalProtectionFee, royalProtectionRule: rpRule, cleaningSizeAddon, cleaningDeepAddon } = useMemo(() => {
        if (!service) return { subtotal: 0, royalProtectionFee: 0, royalProtectionRule: null, cleaningSizeAddon: 0, cleaningDeepAddon: 0 };
        let t;
        if (promoApplied?.override_price != null) {
            t = promoApplied.override_price;
        } else {
            t = isHourly ? service.base_price * hours : service.base_price;
        }
        for (const aid of selectedAddons) {
            const ao = (service.add_ons || []).find((a) => a.id === aid);
            if (ao) t += ao.price;
        }
        if (overnight && isChildcare) t += 2000;
        if (express && !isChildcare) t += 1500;
        // Cleaning property-size surcharge — additive
        let sizeAddon = 0;
        let deepAddon = 0;
        if (isCleaning) {
            sizeAddon = Math.max(0, (parseInt(bedrooms) || 1) - 1) * 1000;
            if (deepClean && isDeepCleanService && (parseInt(bedrooms) || 1) >= 3) {
                deepAddon = 1500;
            }
            t += sizeAddon + deepAddon;
        }
        t += transportFee;
        if (isGrocery && groceryBudget) {
            const b = parseFloat(groceryBudget) || 0;
            t += b + Math.round(b * 0.05);
        }
        const rule = royalProtectionRule(service.category);
        const fee = rule.kind === "flat" ? rule.value : Math.round(t * rule.value);
        return { subtotal: t, royalProtectionFee: fee, royalProtectionRule: rule, cleaningSizeAddon: sizeAddon, cleaningDeepAddon: deepAddon };
    }, [service, isHourly, hours, selectedAddons, overnight, isChildcare, express, isGrocery, groceryBudget, transportFee, promoApplied, bedrooms, deepClean, isCleaning, isDeepCleanService]);

    const grossTotal = subtotal + royalProtectionFee;

    // SMART Coin redemption preview — capped at 50% of grossTotal
    const coinBalance = parseInt(user?.smart_coins || 0);
    const { coinsToRedeem, coinDiscount } = useMemo(() => {
        if (!redeemCoins || coinBalance <= 0 || grossTotal <= 0) return { coinsToRedeem: 0, coinDiscount: 0 };
        const maxJmd = grossTotal * 0.5;
        const maxCoins = Math.floor(maxJmd * 100); // 1 JMD = 100 coins
        const c = Math.min(coinBalance, maxCoins);
        return { coinsToRedeem: c, coinDiscount: c / 100 };
    }, [redeemCoins, coinBalance, grossTotal]);

    // Processing fee — mirror backend gross-up: HandyPay 4.9% + JMD 60.
    // Rounded to 2dp so the summary matches what the backend persists.
    const preFeeTotal = Math.max(0, grossTotal - coinDiscount);
    const processingFee = useMemo(() => {
        if (preFeeTotal <= 0) return 0;
        const t = (preFeeTotal + 60) / (1 - 0.049);
        return Math.round((t - preFeeTotal) * 100) / 100;
    }, [preFeeTotal]);

    const total = Math.round((preFeeTotal + processingFee) * 100) / 100;

    const applyPromo = async () => {
        const code = promoCode.trim();
        if (!code) {
            toast.error("Enter a promo code first");
            return;
        }
        setPromoChecking(true);
        try {
            const { data } = await api.post("/promo/validate", {
                code,
                service_id: serviceId,
            });
            if (data.applies_to_service) {
                setPromoApplied({
                    code: data.code,
                    title: data.title,
                    override_price: data.override_price,
                });
                toast.success(
                    `${data.title || data.code} applied — service price now $${data.override_price.toLocaleString()} JMD`
                );
            } else {
                setPromoApplied(null);
                toast.error(
                    `Code ${data.code} is valid but doesn't apply to ${data.service_name || "this service"}`
                );
            }
        } catch (err) {
            setPromoApplied(null);
            toast.error(err.response?.data?.detail || "Invalid promo code");
        } finally {
            setPromoChecking(false);
        }
    };

    const removePromo = () => {
        setPromoApplied(null);
        setPromoCode("");
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="max-w-7xl mx-auto px-6 py-32 text-muted-foreground overline">Loading...</div>
            </div>
        );
    }

    if (!service) return null;

    const toggleAddon = (id) =>
        setSelectedAddons((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const handleBook = async () => {
        if (!address) {
            toast.error("Please enter your address");
            return;
        }
        if (isChildcare && !childAge) {
            toast.error("Please enter your child's age");
            return;
        }
        setSubmitting(true);
        try {
            const scheduledAt = new Date(date);
            const [h, m] = time.split(":");
            scheduledAt.setHours(parseInt(h), parseInt(m), 0, 0);
            const { data: booking } = await api.post("/bookings", {
                service_id: service.id,
                scheduled_at: scheduledAt.toISOString(),
                address,
                city,
                notes,
                express,
                add_ons: selectedAddons,
                hours: isHourly ? hours : null,
                child_age: isChildcare ? childAge : null,
                overnight: isChildcare ? overnight : false,
                provider_id: selectedProviderId,
                grocery_budget: isGrocery ? parseFloat(groceryBudget) || 0 : null,
                grocery_items: isGrocery ? groceryItems : null,
                bedrooms: isCleaning ? parseInt(bedrooms) || 1 : null,
                deep_clean: isCleaning ? deepClean : false,
                redeem_coins: redeemCoins,
                promo_code: promoApplied?.code || null,
            });
            toast.success("Booking created. Redirecting to payment...");
            const { data: checkout } = await api.post("/payments/handypay/checkout", {
                booking_id: booking.id,
                origin_url: window.location.origin,
            });
            if (isMobile()) {
                await openInAppBrowser(checkout.url);
            } else {
                window.location.href = checkout.url;
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background" data-testid="booking-page">
            <Header />

            <section className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
                <button
                    onClick={() => navigate(-1)}
                    className="overline text-muted-foreground hover:text-foreground mb-8"
                    data-testid="booking-back-btn"
                >
                    ← Back
                </button>

                <div className="grid lg:grid-cols-5 gap-12">
                    {/* Form */}
                    <div className="lg:col-span-3 space-y-10">
                        <div>
                            <div className="overline mb-2">{service.category}</div>
                            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
                                {service.name}
                            </h1>
                            <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                                {service.description}
                            </p>
                            {isHourly && (
                                <div className="mt-4 inline-block px-3 py-1.5 border border-primary/40 text-xs uppercase tracking-[0.2em] gold-text">
                                    Hourly · ${service.base_price.toLocaleString()} JMD/hr
                                </div>
                            )}
                            {isNegotiable && (
                                <div
                                    className="mt-5 p-4 border border-primary/40 bg-primary/5 rounded-sm"
                                    data-testid="negotiable-banner"
                                >
                                    <div className="overline mb-1 gold-text">Negotiable bid</div>
                                    <p className="text-sm text-foreground/90 leading-relaxed">
                                        This trade is priced from <span className="gold-text font-semibold">${service.base_price.toLocaleString()} JMD</span> as a <em>starting offer</em>. Once you submit, qualified pros in your area can <strong>accept your price</strong> or <strong>send a counter-offer</strong> with their hourly rate. You pick the best fit — you're never locked in to the first quote.
                                    </p>
                                    <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                        Counter-offer flow rolling out in app · v1.1
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="hairline" />

                        {/* Cleaning property-size selector */}
                        {isCleaning && (
                            <>
                                <div data-testid="cleaning-size-block">
                                    <div className="overline mb-4">Property Size</div>
                                    <div className="space-y-4">
                                        <div>
                                            <Label className="overline mb-2 block">Bedrooms</Label>
                                            <div className="grid grid-cols-6 gap-2" data-testid="bedrooms-grid">
                                                {[1, 2, 3, 4, 5, "6+"].map((n) => {
                                                    const val = n === "6+" ? 6 : n;
                                                    const active = (parseInt(bedrooms) || 1) === val || (n === "6+" && (parseInt(bedrooms) || 1) >= 6);
                                                    return (
                                                        <button
                                                            key={n}
                                                            type="button"
                                                            onClick={() => setBedrooms(val)}
                                                            data-testid={`bedrooms-${n === "6+" ? "6plus" : n}`}
                                                            className={`h-11 rounded-sm border text-sm transition-all ${
                                                                active
                                                                    ? "border-primary bg-primary/10 text-primary"
                                                                    : "border-white/10 text-muted-foreground hover:border-white/30"
                                                            }`}
                                                        >
                                                            {n}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <p className="mt-2 text-[11px] text-muted-foreground">
                                                +$1,000 JMD per bedroom beyond the first.
                                            </p>
                                        </div>
                                        {isDeepCleanService && (
                                            <label
                                                className="flex items-start gap-3 p-4 border border-white/10 rounded-sm cursor-pointer hover:border-primary/40"
                                                data-testid="deep-clean-toggle-wrap"
                                            >
                                                <Checkbox
                                                    checked={deepClean}
                                                    onCheckedChange={(c) => setDeepClean(Boolean(c))}
                                                    className="mt-1 border-white/20"
                                                    data-testid="deep-clean-toggle"
                                                />
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium">Deep Clean upgrade</div>
                                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                                        Adds +$1,500 JMD when applied to a 3-bedroom or larger home (small homes don&apos;t need it).
                                                    </div>
                                                </div>
                                            </label>
                                        )}
                                    </div>
                                </div>
                                <div className="hairline" />
                            </>
                        )}

                        {/* Grocery options */}
                        {isGrocery && (
                            <>
                                <div>
                                    <div className="overline mb-4">Grocery Order</div>
                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="grocery-budget" className="overline mb-2 block">Budget (JMD)</Label>
                                            <Input
                                                id="grocery-budget"
                                                type="number"
                                                value={groceryBudget}
                                                onChange={(e) => setGroceryBudget(e.target.value)}
                                                placeholder="e.g. 12000"
                                                className="bg-input border-white/10 h-11 rounded-sm"
                                                data-testid="grocery-budget-input"
                                            />
                                            <p className="mt-1 text-xs text-muted-foreground">
                                                Funds your shopper's pre-loaded card. Activated by admin once shopper is dispatched. Unused balance is refunded.
                                            </p>
                                        </div>
                                        <div>
                                            <Label htmlFor="grocery-items" className="overline mb-2 block">Shopping list</Label>
                                            <Textarea
                                                id="grocery-items"
                                                value={groceryItems}
                                                onChange={(e) => setGroceryItems(e.target.value)}
                                                rows={6}
                                                placeholder={"One item per line:\n2 lb chicken breast\n1 box rice\n2 dozen eggs\nGrace condensed milk x3"}
                                                className="bg-input border-white/10 rounded-sm font-mono text-xs"
                                                data-testid="grocery-items-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="hairline" />
                            </>
                        )}

                        {/* Childcare-specific options */}
                        {isChildcare && (
                            <>
                                <div>
                                    <div className="overline mb-4">Care Details</div>
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <div>
                                            <Label htmlFor="child-age" className="overline mb-2 block">Child's age</Label>
                                            <Input
                                                id="child-age"
                                                value={childAge}
                                                onChange={(e) => setChildAge(e.target.value)}
                                                placeholder="e.g. 4 years, 6 months"
                                                className="bg-input border-white/10 h-11 rounded-sm"
                                                data-testid="child-age-input"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between p-4 bg-card border border-white/10 rounded-sm">
                                            <div>
                                                <div className="font-serif text-lg leading-tight">Overnight care</div>
                                                <div className="text-xs text-muted-foreground mt-0.5">+$2,000 JMD surcharge</div>
                                            </div>
                                            <Switch
                                                checked={overnight}
                                                onCheckedChange={setOvernight}
                                                data-testid="overnight-toggle"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="hairline" />

                                {/* Sitter selection */}
                                <div>
                                    <div className="overline mb-4 flex items-center gap-2">
                                        Choose your sitter
                                        <span className="text-muted-foreground normal-case tracking-normal">(optional)</span>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-3" data-testid="sitter-grid">
                                        <SitterCard
                                            selected={selectedProviderId === null}
                                            onClick={() => setSelectedProviderId(null)}
                                            name="Any available pro"
                                            sub="We'll assign the best fit"
                                            testid="sitter-any"
                                        />
                                        {[
                                            ...providers.filter((p) => trustedIds.includes(p.id)),
                                            ...providers.filter((p) => !trustedIds.includes(p.id)),
                                        ].map((p) => (
                                            <SitterCard
                                                key={p.id}
                                                selected={selectedProviderId === p.id}
                                                onClick={() => setSelectedProviderId(p.id)}
                                                name={p.name}
                                                sub={p.bio?.slice(0, 60) || (p.skills || []).slice(0, 3).join(", ")}
                                                trusted={trustedIds.includes(p.id)}
                                                rating={p.rating}
                                                testid={`sitter-${p.id}`}
                                            />
                                        ))}
                                    </div>
                                    {providers.length === 0 && (
                                        <div className="text-xs text-muted-foreground mt-3" data-testid="no-sitters-msg">
                                            No verified sitters listed yet — we'll dispatch one for you.
                                        </div>
                                    )}
                                </div>
                                <div className="hairline" />
                            </>
                        )}

                        {/* Hours selector for hourly services */}
                        {isHourly && (
                            <>
                                <div>
                                    <div className="overline mb-4">Length of Service</div>
                                    <div className="grid grid-cols-5 sm:grid-cols-9 gap-2" data-testid="hours-grid">
                                        {HOURS_OPTIONS.map((h) => (
                                            <button
                                                key={h}
                                                onClick={() => setHours(h)}
                                                className={`py-3 text-sm border rounded-sm transition-all ${
                                                    hours === h
                                                        ? "bg-primary text-primary-foreground border-primary"
                                                        : "bg-card border-white/10 text-muted-foreground hover:text-foreground hover:border-white/30"
                                                }`}
                                                data-testid={`hours-${h}`}
                                            >
                                                {h}h
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="hairline" />
                            </>
                        )}

                        <div>
                            <div className="overline mb-4">{isChildcare ? "Start time" : "01 — Schedule"}</div>
                            <div className="grid sm:grid-cols-2 gap-6">
                                <div className="bg-card border border-white/10 rounded-sm p-2">
                                    <Calendar
                                        mode="single"
                                        selected={date}
                                        onSelect={(d) => d && setDate(d)}
                                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                        className="bg-card text-foreground"
                                        data-testid="booking-calendar"
                                    />
                                </div>
                                <div>
                                    <Label className="overline mb-3 block">Time</Label>
                                    <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                                        {TIME_SLOTS.map((t) => (
                                            <button
                                                key={t}
                                                onClick={() => setTime(t)}
                                                className={`py-3 text-sm border rounded-sm transition-all ${
                                                    time === t
                                                        ? "bg-primary text-primary-foreground border-primary"
                                                        : "bg-card border-white/10 text-muted-foreground hover:text-foreground hover:border-white/30"
                                                }`}
                                                data-testid={`time-slot-${t}`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="hairline" />

                        <div>
                            <div className="overline mb-4">Address</div>
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="address" className="overline mb-2 block">Street address</Label>
                                    <Input
                                        id="address"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        placeholder="12 Mountain Spring Drive"
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        data-testid="booking-address-input"
                                    />
                                </div>
                                <div>
                                    <Label className="overline mb-2 block">City</Label>
                                    <Select value={city} onValueChange={setCity}>
                                        <SelectTrigger
                                            className="bg-input border-white/10 h-11 rounded-sm"
                                            data-testid="booking-city-select"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-card border-white/10">
                                            {CITIES.map((c) => (
                                                <SelectItem key={c} value={c} data-testid={`city-${c}`}>{c}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="notes" className="overline mb-2 block">Notes (optional)</Label>
                                    <Textarea
                                        id="notes"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        rows={3}
                                        placeholder="Gate code, parking, allergies, preferences..."
                                        className="bg-input border-white/10 rounded-sm"
                                        data-testid="booking-notes-input"
                                    />
                                </div>
                            </div>
                        </div>

                        {nearestPros?.providers?.length > 0 && !isChildcare && (
                            <>
                                <div className="hairline" />
                                <div data-testid="nearest-pros-widget">
                                    <div className="overline mb-4 flex items-center gap-2">
                                        Nearest pros
                                        <span className="text-muted-foreground normal-case tracking-normal">
                                            ({nearestPros.total_with_location} verified · sorted by distance)
                                        </span>
                                    </div>
                                    <div className="mb-4">
                                        <NearestProsMap
                                            destination={nearestPros.destination}
                                            providers={nearestPros.providers}
                                            selectedId={selectedProviderId}
                                            onSelect={setSelectedProviderId}
                                            height={300}
                                        />
                                    </div>
                                    <div className="grid sm:grid-cols-3 gap-3">
                                        {nearestPros.providers.map((p) => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                onClick={() => setSelectedProviderId(selectedProviderId === p.id ? null : p.id)}
                                                className={`text-left p-4 border rounded-sm transition-all ${
                                                    selectedProviderId === p.id
                                                        ? "border-primary bg-primary/5"
                                                        : "border-white/10 bg-card hover:border-white/30"
                                                }`}
                                                data-testid={`nearest-pro-${p.id}`}
                                            >
                                                <div className="flex items-baseline justify-between gap-2">
                                                    <div className="font-serif text-lg leading-tight truncate">{p.name}</div>
                                                    <span className="text-[10px] uppercase tracking-[0.2em] gold-text whitespace-nowrap">
                                                        {p.distance_km}km · {p.eta_minutes}min
                                                    </span>
                                                </div>
                                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                                    <RoyalProClassBadge provider={p} size="sm" />
                                                    {p.rating > 0 && (
                                                        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                                            ★ {p.rating.toFixed(1)} ({p.review_count})
                                                        </span>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    <p className="mt-3 text-[11px] text-muted-foreground">
                                        Tap a pin or card to request a specific pro — leave unselected and we'll dispatch the best fit.
                                    </p>
                                </div>
                            </>
                        )}

                        {hasAddons && (
                            <>
                                <div className="hairline" />
                                <div>
                                    <div className="overline mb-4">Add-on Services</div>
                                    <div className="grid sm:grid-cols-2 gap-3" data-testid="addons-grid">
                                        {service.add_ons.map((a) => {
                                            const checked = selectedAddons.includes(a.id);
                                            return (
                                                <label
                                                    key={a.id}
                                                    className={`flex items-start justify-between gap-3 p-4 border rounded-sm cursor-pointer transition-all ${
                                                        checked
                                                            ? "border-primary bg-primary/5"
                                                            : "border-white/10 bg-card hover:border-white/30"
                                                    }`}
                                                    data-testid={`addon-${a.id}`}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <Checkbox
                                                            checked={checked}
                                                            onCheckedChange={() => toggleAddon(a.id)}
                                                            className="border-white/20 mt-0.5"
                                                        />
                                                        <div>
                                                            <div className="text-sm">{a.label}</div>
                                                            <div className="text-xs text-muted-foreground mt-1">
                                                                +${a.price.toLocaleString()} JMD
                                                            </div>
                                                        </div>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        )}

                        {!isChildcare && (
                            <>
                                <div className="hairline" />
                                <div>
                                    <div className="overline mb-4">Express</div>
                                    <div
                                        className="flex items-center justify-between p-5 bg-card border border-white/10 rounded-sm"
                                        data-testid="express-toggle-row"
                                    >
                                        <div>
                                            <div className="font-serif text-xl">Express 1-Hour Response</div>
                                            <div className="text-xs text-muted-foreground mt-1">+$1,500 JMD · Pro arrives within 60 min</div>
                                        </div>
                                        <Switch checked={express} onCheckedChange={setExpress} data-testid="express-toggle" />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Summary */}
                    <aside className="lg:col-span-2">
                        <div className="lg:sticky lg:top-28 bg-card border border-white/10 p-8 rounded-sm" data-testid="booking-summary">
                            <div className="overline mb-4">Order Summary</div>
                            <div className="space-y-3 text-sm">
                                <Row
                                    label={service.name}
                                    value={
                                        isHourly
                                            ? `$${service.base_price.toLocaleString()} × ${hours}h`
                                            : `$${service.base_price.toLocaleString()}`
                                    }
                                />
                                {selectedAddons.map((aid) => {
                                    const ao = service.add_ons.find((a) => a.id === aid);
                                    if (!ao) return null;
                                    return (
                                        <Row key={aid} label={`+ ${ao.label}`} value={`$${ao.price.toLocaleString()}`} />
                                    );
                                })}
                                {overnight && isChildcare && <Row label="+ Overnight" value="$2,000" />}
                                {express && !isChildcare && <Row label="Express +" value="$1,500" />}
                                <Row
                                    label={
                                        <span className="flex items-center gap-2">
                                            Transport fee
                                            {transportQuote?.distance_km != null && (
                                                <span className="text-[10px] uppercase tracking-[0.2em] gold-text">
                                                    {transportQuote.distance_km}km · {transportQuote.eta_minutes}min
                                                </span>
                                            )}
                                        </span>
                                    }
                                    value={
                                        quoteLoading
                                            ? <span className="text-muted-foreground">calculating…</span>
                                            : `$${transportFee.toLocaleString()}`
                                    }
                                />
                                <div className="hairline my-3" />
                                <Row
                                    label={<span className="overline">Date</span>}
                                    value={format(date, "MMM d, yyyy")}
                                />
                                <Row label={<span className="overline">Time</span>} value={time} />
                                {isHourly && (
                                    <Row label={<span className="overline">Hours</span>} value={`${hours}h`} />
                                )}
                                {isChildcare && childAge && (
                                    <Row label={<span className="overline">Child age</span>} value={childAge} />
                                )}
                                <Row label={<span className="overline">City</span>} value={city} />
                                <div className="hairline my-3" />

                                {/* Promo code */}
                                <div className="mb-2" data-testid="promo-section">
                                    <div className="overline mb-2">Promo code</div>
                                    {promoApplied ? (
                                        <div
                                            className="flex items-center justify-between gap-2 px-3 py-2 bg-primary/10 border border-primary/30 rounded-sm"
                                            data-testid="promo-applied-pill"
                                        >
                                            <div className="min-w-0">
                                                <div className="text-xs gold-text font-semibold tracking-wide">
                                                    {promoApplied.code}
                                                </div>
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground truncate">
                                                    {promoApplied.title || "Applied"}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={removePromo}
                                                className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-destructive"
                                                data-testid="promo-remove-btn"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex gap-2">
                                            <Input
                                                value={promoCode}
                                                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                                placeholder="MOMROYAL"
                                                className="bg-input border-white/10 rounded-sm h-10 text-sm uppercase tracking-wider flex-1"
                                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyPromo())}
                                                data-testid="promo-input"
                                            />
                                            <Button
                                                type="button"
                                                onClick={applyPromo}
                                                disabled={promoChecking || !promoCode.trim()}
                                                className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-10 px-4 bg-primary text-primary-foreground hover:bg-primary/90"
                                                data-testid="promo-apply-btn"
                                            >
                                                {promoChecking ? "..." : "Apply"}
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                <div className="hairline my-3" />
                                <Row
                                    label={
                                        <span className="flex items-center gap-2">
                                            Royal Protection &amp; Insurance
                                            {rpRule && (
                                                <span className="text-[10px] uppercase tracking-[0.2em] gold-text">
                                                    {rpRule.kind === "flat" ? "Flat" : `${(rpRule.value * 100).toFixed(rpRule.value === 0.125 ? 1 : 0)}%`}
                                                </span>
                                            )}
                                        </span>
                                    }
                                    value={`$${royalProtectionFee.toLocaleString()}`}
                                />

                                {/* Cleaning property-size breakdown */}
                                {isCleaning && cleaningSizeAddon > 0 && (
                                    <Row
                                        label={<span className="overline">+ Bedrooms x{Math.max(0, (parseInt(bedrooms) || 1) - 1)}</span>}
                                        value={`$${cleaningSizeAddon.toLocaleString()}`}
                                    />
                                )}
                                {isCleaning && cleaningDeepAddon > 0 && (
                                    <Row
                                        label={<span className="overline gold-text">+ Deep Clean 3+ BR</span>}
                                        value={`$${cleaningDeepAddon.toLocaleString()}`}
                                    />
                                )}

                                {/* SMART Coin redemption toggle */}
                                {user && coinBalance > 0 && (
                                    <div
                                        className="mt-4 p-4 border border-amber-700/30 rounded-sm bg-amber-900/10"
                                        data-testid="coin-redemption-block"
                                    >
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <Checkbox
                                                checked={redeemCoins}
                                                onCheckedChange={(c) => setRedeemCoins(Boolean(c))}
                                                className="mt-1 border-amber-700/50"
                                                data-testid="coin-redemption-toggle"
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <Coins size={14} className="gold-text" strokeWidth={1.5} />
                                                    <span className="text-sm font-medium">
                                                        Redeem SMART Coins
                                                    </span>
                                                    <span
                                                        className="text-[10px] uppercase tracking-[0.2em] text-amber-300"
                                                        data-testid="coin-balance-pill"
                                                    >
                                                        {coinBalance.toLocaleString()} avail
                                                    </span>
                                                </div>
                                                <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                                                    {redeemCoins && coinsToRedeem > 0 ? (
                                                        <span data-testid="coin-discount-applied">
                                                            Redeeming{" "}
                                                            <strong className="gold-text">
                                                                {coinsToRedeem.toLocaleString()}
                                                            </strong>{" "}
                                                            coins for{" "}
                                                            <strong className="gold-text">
                                                                ${coinDiscount.toLocaleString()}
                                                            </strong>{" "}
                                                            off (capped at 50% of total).
                                                        </span>
                                                    ) : (
                                                        <>1,000 coins = $10 JMD. Redemption is capped at 50% of your booking total.</>
                                                    )}
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                )}

                                {/* Card processing fee — pass-through so platform + pro stay whole */}
                                {processingFee > 0 && (
                                    <Row
                                        label={
                                            <span className="flex items-center gap-2">
                                                Processing Fee
                                                <span className="text-[10px] uppercase tracking-[0.2em] gold-text">
                                                    4.9% + $60
                                                </span>
                                            </span>
                                        }
                                        value={
                                            <span data-testid="processing-fee-value">
                                                ${processingFee.toLocaleString()}
                                            </span>
                                        }
                                    />
                                )}

                                <div className="flex items-end justify-between mt-3" data-testid="booking-total-block">
                                    <div>
                                        <div className="overline">Total (JMD)</div>
                                        <div className="font-serif text-4xl mt-1" data-testid="booking-total">
                                            <span className="gold-text">$</span>
                                            {total.toLocaleString()}
                                        </div>
                                        {redeemCoins && coinDiscount > 0 && (
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 mt-1" data-testid="coin-discount-label">
                                                After ${coinDiscount.toLocaleString()} SMART Coin discount
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <Button
                                onClick={handleBook}
                                disabled={submitting}
                                className="w-full mt-8 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-xs h-12"
                                data-testid="confirm-booking-btn"
                            >
                                {submitting ? "Processing..." : "Confirm & Pay"} <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
                            </Button>
                            <ul className="mt-6 space-y-2 text-xs text-muted-foreground">
                                {["Verified pro", "Insured & bonded", "Cancel up to 2hr before"].map((p) => (
                                    <li key={p} className="flex items-center gap-2">
                                        <CheckCircle2 size={12} className="gold-text" strokeWidth={1.5} /> {p}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between gap-4">
            <div className="text-muted-foreground">{label}</div>
            <div className="text-foreground">{value}</div>
        </div>
    );
}

function SitterCard({ selected, onClick, name, sub, trusted, rating, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left p-4 border rounded-sm transition-all relative ${
                selected
                    ? "border-primary bg-primary/5"
                    : "border-white/10 bg-card hover:border-white/30"
            }`}
            data-testid={testid}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                    <div className="font-serif text-lg leading-tight">{name}</div>
                    {sub && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{sub}</div>}
                </div>
                {trusted && (
                    <Heart size={14} strokeWidth={1.5} fill="currentColor" className="gold-text flex-shrink-0" />
                )}
            </div>
            {(trusted || rating > 0) && (
                <div className="mt-2 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em]">
                    {trusted && <span className="gold-text">Your trusted</span>}
                    {rating > 0 && <span className="text-muted-foreground">★ {rating.toFixed(1)}</span>}
                </div>
            )}
        </button>
    );
}
